import React from 'react'
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  IconButton,
  Stack,
  Divider,
  Avatar,
  LinearProgress,
  Box,
  CircularProgress,
  Tooltip,
} from '@mui/material'
import {
  MoreVert as MoreVertIcon,
  Visibility as ViewIcon,
  Payment as PaymentIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { Invoice } from '../../types/payment'
import { getStatusConfig, StatusConfig } from './InvoiceStatusConfig'
import {
  calculatePaymentProgress,
  isInvoiceOverdue,
  getOutstandingAmount,
  formatInvoiceDate,
  formatCurrency,
} from '../../utils/invoiceUtils'

const STATUS_HEX: Record<StatusConfig['color'], string> = {
  default: '#9e9e9e',
  primary: '#1976d2',
  secondary: '#9c27b0',
  error: '#d32f2f',
  info: '#0288d1',
  success: '#2e7d32',
  warning: '#ed6c02',
}

interface InvoiceCardProps {
  invoice: Invoice
  onCardClick: (id: string) => void
  onMenuClick: (event: React.MouseEvent<HTMLElement>, invoice: Invoice) => void
  onPaymentClick: (invoice: Invoice) => void
  isActionLoading?: boolean
}

const InvoiceCard: React.FC<InvoiceCardProps> = ({
  invoice,
  onCardClick,
  onMenuClick,
  onPaymentClick,
  isActionLoading = false,
}) => {
  const statusConfig: StatusConfig = getStatusConfig(invoice.status)
  const paymentProgress = calculatePaymentProgress(invoice)
  const overdueStatus = isInvoiceOverdue(invoice.due_date, invoice.status)
  const outstandingAmount = getOutstandingAmount(invoice)
  const accentHex = overdueStatus ? STATUS_HEX.error : STATUS_HEX[statusConfig.color]
  const customerName = invoice.customer_name || invoice.customer?.name || 'N/A'
  const initials = customerName.split(' ').filter(Boolean).slice(0, 2).map((w: string) => w[0]).join('').toUpperCase()

  return (
    <Card
      role="article"
      aria-label={`Invoice ${invoice.invoice_number}`}
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onCardClick(invoice.id)
        }
      }}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        cursor: 'pointer',
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2.5,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 1px 3px rgba(15,23,42,0.06)',
        transition: 'box-shadow 0.25s ease, transform 0.25s ease, border-color 0.25s ease',
        '&:hover': {
          boxShadow: `0 12px 28px -8px ${accentHex}55`,
          transform: 'translateY(-3px)',
          borderColor: accentHex,
        },
        '&:focus': {
          outline: '2px solid',
          outlineColor: 'primary.main',
          outlineOffset: '2px',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 4,
          bgcolor: accentHex,
        },
      }}
      onClick={() => onCardClick(invoice.id)}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1, pt: 2.5 }}>
        {/* Header with Invoice Number and Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.5 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700} letterSpacing={0.2}>
              {invoice.invoice_number}
            </Typography>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
              <Chip
                icon={statusConfig.icon as React.ReactElement}
                label={statusConfig.label}
                size="small"
                sx={{
                  borderRadius: 4,
                  fontWeight: 600,
                  bgcolor: `${STATUS_HEX[statusConfig.color]}1A`,
                  color: STATUS_HEX[statusConfig.color],
                  '& .MuiChip-icon': { color: STATUS_HEX[statusConfig.color] },
                }}
              />
              {overdueStatus && (
                <Chip
                  icon={<WarningIcon fontSize="small" />}
                  label="Overdue"
                  color="error"
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 4, fontWeight: 600 }}
                />
              )}
            </Stack>
          </Box>
          <IconButton
            size="small"
            aria-label={`More actions for invoice ${invoice.invoice_number}`}
            onClick={(e) => {
              e.stopPropagation()
              onMenuClick(e, invoice)
            }}
            sx={{ mt: -0.5, mr: -1 }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        </Box>

        {/* Customer Info */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, mb: 1.75 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: '0.8rem',
              fontWeight: 700,
              bgcolor: `${accentHex}1A`,
              color: accentHex,
            }}
          >
            {initials || <PersonIcon fontSize="small" />}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} noWrap>
              {customerName}
            </Typography>
            {invoice.sales_order_number ? (
              <Typography variant="caption" color="info.main" fontWeight={500} noWrap sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <DescriptionIcon sx={{ fontSize: 12 }} /> from {invoice.sales_order_number}
              </Typography>
            ) : (
              <Typography variant="caption" color="text.secondary">Direct Invoice</Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Dates */}
        <Box sx={{ display: 'flex', gap: 2, mb: 1.75 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <CalendarIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              <Typography variant="caption" color="text.disabled">
                Invoice Date
              </Typography>
            </Box>
            <Typography variant="body2" fontWeight={500}>
              {formatInvoiceDate(invoice.invoice_date)}
            </Typography>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25 }}>
              <ScheduleIcon sx={{ fontSize: 13, color: overdueStatus ? 'error.main' : 'text.disabled' }} />
              <Typography variant="caption" color={overdueStatus ? 'error.main' : 'text.disabled'}>
                Due Date
              </Typography>
            </Box>
            <Typography
              variant="body2"
              fontWeight={500}
              color={overdueStatus ? 'error.main' : 'text.primary'}
            >
              {formatInvoiceDate(invoice.due_date)}
            </Typography>
          </Box>
        </Box>

        {/* Amount and Payment Progress */}
        <Box sx={{ bgcolor: 'rgba(46, 125, 50, 0.08)', borderRadius: 1.5, px: 1.5, py: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <MoneyIcon sx={{ fontSize: 16, color: 'success.main' }} />
              <Typography variant="caption" color="text.secondary" fontWeight={500}>
                Total Amount
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              {paymentProgress.toFixed(0)}% paid
            </Typography>
          </Box>
          <Typography variant="subtitle1" fontWeight={700} color="success.dark" gutterBottom>
            ₹{formatCurrency(invoice.total_amount)}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={paymentProgress}
            sx={{
              height: 6,
              borderRadius: 3,
              backgroundColor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                borderRadius: 3,
                backgroundColor: paymentProgress === 100 ? 'success.main' : 'info.main'
              }
            }}
          />
          {invoice.paid_amount > 0 && invoice.paid_amount < invoice.total_amount && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Paid: ₹{formatCurrency(invoice.paid_amount)} |
              Balance: ₹{formatCurrency(outstandingAmount)}
            </Typography>
          )}
        </Box>
      </CardContent>

      <CardActions sx={{ px: 2, pb: 1.5, pt: 0.5, gap: 0.5 }}>
        <Tooltip title="View Invoice">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              onCardClick(invoice.id)
            }}
            sx={{ border: '1px solid', borderColor: 'divider' }}
            aria-label={`View invoice ${invoice.invoice_number}`}
          >
            <ViewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }} />
        {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
          <Tooltip title={isActionLoading ? 'Processing...' : 'Record Payment'}>
            <span>
              <IconButton
                size="small"
                disabled={isActionLoading}
                onClick={(e) => {
                  e.stopPropagation()
                  onPaymentClick(invoice)
                }}
                sx={{ border: '1px solid', borderColor: 'success.main', color: 'success.main' }}
                aria-label={`Record payment for invoice ${invoice.invoice_number}`}
              >
                {isActionLoading ? <CircularProgress size={16} /> : <PaymentIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}
      </CardActions>
    </Card>
  )
}

export default InvoiceCard
